import { Component } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialogRef } from "@angular/material/dialog";
import { Select, Store } from "@ngxs/store";
import { firstValueFrom, Observable } from "rxjs";
import { FirebaseUser } from "app/interface/user";
import {
  ResetPasswordEmail,
  SetDarkModePreference,
  SignOut,
} from "app/states/actions/user.actions";
import { UserState } from "app/states/user.state";

@Component({
  selector: "app-profile-modal",
  templateUrl: "./profile-modal.component.html",
  styleUrls: ["./profile-modal.component.less"],
  standalone: false,
})
export class ProfileModalComponent {
  @Select(UserState.firebaseUser) firebaseUser$: Observable<FirebaseUser>;
  loadingTheme = false;
  sendingReset = false;

  constructor(
    private readonly store: Store,
    private readonly snackBar: MatSnackBar,
    private readonly dialogRef: MatDialogRef<ProfileModalComponent>,
  ) {}

  getFullName(fb: FirebaseUser): string {
    const first =
      fb?.AppUser?.givenName || fb?.AppUser?.Person?.Name_Person || "";
    const last =
      fb?.AppUser?.familyName || fb?.AppUser?.Person?.Lastname_Person || "";
    return `${first} ${last}`.trim() || "Usuario";
  }

  getInitials(fb: FirebaseUser): string {
    const fullName = this.getFullName(fb);
    if (!fullName || fullName === "Usuario") {
      return (fb?.AppUser?.email?.[0] || "?").toUpperCase();
    }
    const parts = fullName.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || parts[0]?.[1] || "";
    return `${first}${second}`.toUpperCase();
  }

  close(): void {
    this.dialogRef.close();
  }

  async toggleTheme(enabled: boolean) {
    if (this.loadingTheme) return;
    this.loadingTheme = true;
    try {
      await firstValueFrom(this.store.dispatch(new SetDarkModePreference(enabled)));
      this.snackBar.open(
        enabled ? "Modo oscuro activado" : "Modo claro activado",
        "OK",
        { duration: 2500, panelClass: ["custom-snackbar"] },
      );
    } catch (err: unknown) {
      this.snackBar.open(
        `No se pudo actualizar el tema: ${err instanceof Error ? err.message : String(err ?? "")}`,
        "OK",
        { duration: 3500, panelClass: ["custom-snackbar-error"] },
      );
    } finally {
      this.loadingTheme = false;
    }
  }

  async resetPassword(email?: string | null) {
    if (!email || this.sendingReset) return;
    this.sendingReset = true;
    try {
      await firstValueFrom(this.store.dispatch(new ResetPasswordEmail(email)));
    } finally {
      this.sendingReset = false;
    }
  }

  logout() {
    this.dialogRef.close();
    this.store.dispatch(new SignOut());
  }
}
